import { useUpdateResultsQuery } from '#pplx/frontend/apps/ask/all-results/hooks/useAllResultsQuery.js';
import { useMutateEntries, useMutateEntry } from '#pplx/frontend/stream/stream.js';
import { createContext, useCallback, useContext, useMemo } from 'react';
export const resultsContext = createContext(null);
resultsContext.displayName = 'ResultsContext';
export function useResults() {
    const context = useContext(resultsContext);
    if (!context)
        throw new Error('useResults must be used within a ResultsProvider');
    return context.useProxy();
}
export function useResultsSelector(selector) {
    const context = useContext(resultsContext);
    if (!context)
        throw new Error('useResultsSelector must be used within a ResultsProvider');
    return context.useSelector(selector);
}
export function useResultsActions() {
    const context = useContext(resultsContext);
    if (!context)
        throw new Error('useResultsActions must be used within a ResultsProvider');
    return context.actions;
}
export function useThreadMetadata() {
    return useContext(resultsContext)?.threadMetadata ?? null;
}
export function useLoadedResultsGetter() {
    return useContext(resultsContext)?.getLoadedResults;
}
export function useCurrentResultIds() {
    const context = useContext(resultsContext);
    return useMemo(() => ({
        currentStreamId: context?.currentStreamId,
        currentThreadId: context?.currentThreadId,
    }), [context?.currentStreamId, context?.currentThreadId]);
}
export const useMutateResults = () => {
    const context = useContext(resultsContext);
    const mutateEntries = useMutateEntries();
    const updateResultsQuery = useUpdateResultsQuery();
    return useCallback((updater) => mutateEntries(context?.currentThreadId, (oldResults) => {
        const newResults = updater(oldResults);
        if (!newResults)
            return;
        updateResultsQuery(undefined, newResults);
        return newResults;
    }), [mutateEntries, context?.currentThreadId, updateResultsQuery]);
};
export const useMutateResult = () => {
    const mutateEntry = useMutateEntry();
    const updateResultsQuery = useUpdateResultsQuery();
    return useCallback((resultUUID, updater) => {
        mutateEntry(resultUUID, (oldResult) => {
            const newResult = updater(oldResult);
            if (!newResult)
                return;
            updateResultsQuery(newResult, undefined);
            return newResult;
        });
    }, [mutateEntry, updateResultsQuery]);
};
//# sourceMappingURL=ResultsContext.js.map